<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { mapState } from "vuex";
import { PAYMENT_METHOD_MODULE } from "@/state/modules/paymentMethod";
import { paymentMethodMethods } from "@/state/helpers";

export default {
  page() {
    return { title: i18n.t("module.paymentMethod") };
  },
  components: { Layout, PageHeader },
  data() {
    return {
      fields: [
        { key: "imageUrl", label: i18n.t("paymentMethod.image") },
        { key: "name", label: i18n.t("label.name") },
        { key: "type", label: i18n.t("paymentMethod.type") },
        { key: "details", label: i18n.t("paymentMethod.details") },
        { key: "sortOrder", label: i18n.t("paymentMethod.sortOrder") },
        { key: "isActive", label: i18n.t("label.status") },
        { key: "action", label: i18n.t("label.action") },
      ],
    };
  },
  computed: {
    ...mapState(PAYMENT_METHOD_MODULE, ["paymentMethods"]),
    tableRows() {
      return this.paymentMethods.data;
    },
    title() {
      return i18n.t("module.paymentMethod");
    },
    breadcrumbItems() {
      return [
        { text: i18n.t("module.admin"), href: "/" },
        { text: this.title, active: true },
      ];
    },
  },
  async created() {
    // Inactive ones included: this is where they are switched back on.
    await this.getAllPaymentMethods({ params: { include_inactive: 1 } });
  },
  methods: {
    ...paymentMethodMethods,
    typeLabel(type) {
      const map = { cash: "typeCash", bank: "typeBank", qr: "typeQr" };
      return i18n.t(`paymentMethod.${map[type] || "typeCash"}`);
    },
    typeVariant(type) {
      return { cash: "success", bank: "info", qr: "warning" }[type] || "secondary";
    },
    goCreate() {
      this.$router.push({ name: "Payment-method-create" });
    },
    goEdit(row) {
      this.$router.push({ name: "Payment-method-update", params: { uuid: row.uuid } });
    },
    async remove(row) {
      const ok = await this.$bvModal.msgBoxConfirm(
        this.$t("label.deleteX", [row.name]) + "?",
        {
          okTitle: this.$t("label.delete"),
          cancelTitle: this.$t("label.cancel"),
          okVariant: "danger",
          centered: true,
        }
      );
      if (!ok) return;
      await this.deletePaymentMethod({ uuid: row.uuid });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <p class="text-muted mb-0">
                <i class="mdi mdi-information-outline mr-1"></i>{{ $t('paymentMethod.cashHint') }}
              </p>
              <b-button v-if="$can('Create Payment Method')" variant="primary" size="sm" @click="goCreate">
                <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.paymentMethod')]) }}
              </b-button>
            </div>

            <b-table :items="tableRows" :fields="fields" responsive="sm" small show-empty>
              <template #empty>
                <div class="text-center text-muted py-3">{{ $t('paymentMethod.noMethods') }}</div>
              </template>

              <template #cell(imageUrl)="data">
                <img
                  v-if="tableRows[data.index].imageUrl"
                  :src="tableRows[data.index].imageUrl"
                  style="height: 40px; max-width: 60px; object-fit: contain;"
                  :alt="tableRows[data.index].name"
                />
                <span v-else class="text-muted">—</span>
              </template>

              <template #cell(type)="data">
                <b-badge :variant="typeVariant(tableRows[data.index].type)">
                  {{ typeLabel(tableRows[data.index].type) }}
                </b-badge>
              </template>

              <template #cell(details)="data">
                <span v-if="!tableRows[data.index].details.length" class="text-muted">—</span>
                <div v-else>
                  <div v-for="(d, di) in tableRows[data.index].details" :key="di" class="small">
                    <span class="text-muted">{{ d.label }}:</span> {{ d.value }}
                  </div>
                </div>
              </template>

              <template #cell(isActive)="data">
                <b-badge :variant="tableRows[data.index].isActive ? 'success' : 'secondary'">
                  {{ tableRows[data.index].isActive ? $t('label.active') : $t('label.inactive') }}
                </b-badge>
              </template>

              <template #cell(action)="data">
                <b-button
                  v-if="$can('Update Payment Method')"
                  size="sm"
                  variant="outline-primary"
                  class="mr-1"
                  @click="goEdit(tableRows[data.index])"
                >
                  <i class="mdi mdi-pencil"></i>
                </b-button>
                <b-button
                  v-if="$can('Delete Payment Method')"
                  size="sm"
                  variant="outline-danger"
                  @click="remove(tableRows[data.index])"
                >
                  <i class="mdi mdi-trash-can-outline"></i>
                </b-button>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
